import { HostListener, Injectable } from '@angular/core';
import { StackingPlanService } from '../../../stacking-plan.service';
import { Executable } from '../executable';
import { NotificationService } from '../../../shared/services/notification.service';
import { UpdateStackingPlanInfo } from '../../floor-container/floor/floor.model';
import { MoreInfoDialogComponent } from '../../dialogs/more-info-dialog/more-info-dialog.component';
import { filter, Observable, switchMap, map, concat, of, takeLast } from 'rxjs';
import { ReqPropertyInfo, Toolbar } from '../actions-toolbar.model';
import { ActionsToolbarService } from '../actions-toolbar.service';
import { LocalizationOptionsService } from '../../../localization-option.service';
import { FloorContainerRepository } from '../../floor-container/floor-container-repository.service';
import { ElevatorBanksRepository } from '../../floor-container/elevator-banks/elevator-banks-repository.service';
import { ElevatorChangesHelperService } from '../helpers/elevator-changes.helper';
import { FloorDataChangesHelperService } from '../helpers/floor-data-changes.helper';

/**
 * Represents a directive that handles the export functionality.
 */
@Injectable()
export class UpdateBuildingExecutableService implements Executable {
  constructor(
    private notificationService: NotificationService,
    private stackingPlanService: StackingPlanService,
    private localizationOptions: LocalizationOptionsService,
    private actionsToolbarService: ActionsToolbarService,
    private floorContainerRepository: FloorContainerRepository,
    private elevatorBanksRepository: ElevatorBanksRepository,
    private elevatorChangesHelper: ElevatorChangesHelperService,
    private floorDataChangesHelper: FloorDataChangesHelperService
  ) {}

  get floorsElevatorsInfo() {
    return {
      floorInfo: this.floorContainerRepository.floorData,
      processedElevatorInfo: this.elevatorBanksRepository.elevatorBanksMatrix,
      elevatorInfo: this.elevatorBanksRepository.elevatorBanks,
    };
  }
  /**
   * Open the export dialog. On close and if the user clicked on export, it
   * requests to get the exported data with the selected configuration
   */
  @HostListener('click', ['$event'])
  execute(): void {
    const localeId = this.localizationOptions.getInstance()?.localeId;
    const floorsElevatorsInfo = this.floorsElevatorsInfo;

    const floorsHeaderData = this.stackingPlanService.toolbarData;
    const data = { floorsHeaderData, floorsElevatorsInfo, localeId };
    this.notificationService
      .openDialog<UpdateStackingPlanInfo>(MoreInfoDialogComponent, data, {
        panelClass: ['table-modals', 'sp-manage-floors-dialog'],
      })
      .pipe(filter(Boolean), switchMap(this._handleMoreInfoDialogClose))
      .subscribe(modifiedSPInfo => {
        /*
         * If any changes are made for toolbar data, floor data or elevator data
         * setUpdatedStackingData is called so the data in stacking plan
         * container gets updated
         */
        this.stackingPlanService.setUpdatedStackingData({ toolbarData: modifiedSPInfo.toolbarData });
        this.stackingPlanService.setSpaceChanges();
      });
  }

  private _handleMoreInfoDialogClose = (modifiedSPInfo: UpdateStackingPlanInfo) => {
    const { updatedFloorData, elevatorBank, deletedElevators } = modifiedSPInfo;
    const { floorInfo, elevatorInfo } = this.floorsElevatorsInfo;
    const requestsToBeMade: Array<Observable<any>> = [];

    if (modifiedSPInfo.toolbarData != null && modifiedSPInfo.headerChanged !== false) {
      const updateUndergroundFloors$ = this.updateUndergroundFloors(modifiedSPInfo.toolbarData);
      if (updateUndergroundFloors$ != null) {
        requestsToBeMade.push(updateUndergroundFloors$);
      }
    }

    // Get the changes to be made for the updated Floor Data (changes related to floors)
    requestsToBeMade.push(...this.floorDataChangesHelper.getUpdatedFloorData(updatedFloorData, floorInfo));

    // Requests made for elevator needs to follow the order of delete and then create
    const elevatorRequests = this.elevatorChangesHelper.getElevatorRequests(
      elevatorBank,
      deletedElevators,
      elevatorInfo
    );

    if (elevatorRequests.length > 0) {
      requestsToBeMade.push(concat(...elevatorRequests));
    }
    if (requestsToBeMade.length === 0) {
      return of(modifiedSPInfo);
    }
    // one refresh per save, after every write (serial order kept for MIQ-26431)
    return concat(...requestsToBeMade).pipe(
      takeLast(1),
      map(() => modifiedSPInfo)
    );
  };

  private updateUndergroundFloors(toolbarChanges: Toolbar): Observable<any> {
    const {
      underGroundFloors,
      totalNumberOfFloors,
      netRentableArea,
      netRentableAreaUOM,
      commonFloorSize,
      commonFloorSizeUOM,
    } = toolbarChanges;
    if (underGroundFloors != null) {
      const reqUndergroundFloors = new ReqPropertyInfo(
        this.stackingPlanService.propertyId,
        underGroundFloors,
        totalNumberOfFloors,
        netRentableArea,
        netRentableAreaUOM,
        commonFloorSize,
        commonFloorSizeUOM
      );
      return this.actionsToolbarService
        .updateUndergroundFloors(reqUndergroundFloors)
        .pipe(this.actionsToolbarService.errorHandling('errorMessages.updateUndergroundFloor'));
    }
  }
}
